import { ELECTROMAGNET_DEFAULT_CYCLE_SECONDS } from '@md/shared';
import { z } from 'zod';

/**
 * Schémas zod des entités PROPRES à Magnetic Duo (concept §8.1 :
 * `games/magnetic-duo/entities/`).
 *
 * Le moteur ne connaît ni la polarité ni les électroaimants : ces schémas sont
 * la frontière de confiance entre le JSON d'un niveau et les factories du
 * module.
 */

/** Polarité écrite dans les niveaux : `+` = Nord, `-` = Sud. */
export const PolaritySchema = z.union([z.literal('+'), z.literal('-')]);

/** Convertit la polarité du JSON en signe numérique. */
export function polaritySign(polarity: '+' | '-'): number {
  return polarity === '+' ? 1 : -1;
}

const base = z.object({
  x: z.number(),
  y: z.number(),
  id: z.string().min(1).optional(),
});

/** Bloc métallique polarisé, attirable / repoussable à distance. */
export const BlockDefSchema = base.extend({
  type: z.literal('block'),
  polarity: PolaritySchema.default('+'),
});

/** Électroaimant fixé au décor : cycle automatique ou commandé par un signal. */
export const ElectromagnetDefSchema = base.extend({
  type: z.literal('electromagnet'),
  polarity: PolaritySchema.default('+'),
  /** Demi-période du cycle, en secondes. Ignoré si `linkedTo` est présent. */
  cycleSeconds: z.number().positive().default(ELECTROMAGNET_DEFAULT_CYCLE_SECONDS),
  /** Décalage du cycle, en secondes (permet de désynchroniser deux aimants). */
  phaseSeconds: z.number().min(0).default(0),
  linkedTo: z.string().min(1).optional(),
  /** `true` : toujours actif (ni cycle ni signal). */
  always: z.boolean().default(false),
});

/** Levier polarisé : actionnable uniquement à distance, par le bon pôle. */
export const LeverDefSchema = base.extend({
  type: z.literal('lever'),
  id: z.string().min(1),
  polarity: PolaritySchema.default('+'),
});

/** Boulon : collectible de la couleur d'un des deux robots. */
export const BoltDefSchema = base.extend({
  type: z.literal('bolt'),
  color: z.union([z.literal('red'), z.literal('blue')]),
});

export type BlockDef = z.infer<typeof BlockDefSchema>;
export type ElectromagnetDef = z.infer<typeof ElectromagnetDefSchema>;
export type LeverDef = z.infer<typeof LeverDefSchema>;
export type BoltDef = z.infer<typeof BoltDefSchema>;
